import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Droplets, LayoutGrid, ScrollText } from 'lucide-react';

const devItems = [
  {
    slug: 'lsm-tree',
    title: 'LSM Tree',
    subtitle: 'Log-Structured Merge Tree',
    description:
      'Watch writes land in the MemTable, flush to SSTables, bloom filters get built, ' +
      'sparse indexes get created, and compaction clean up stale data — all step by step.',
    tags: ['Storage Engines', 'Databases', 'Data Structures'],
    icon: Layers,
    steps: 20,
    group: 'Storage',
  },
  {
    slug: 'token-bucket',
    title: 'Token Bucket',
    subtitle: 'Rate Limiting Algorithm',
    description:
      'See how a bucket of tokens drains on each request and refills over time. ' +
      'Observe burst absorption, empty-bucket rejections, cap enforcement, ' +
      'and graceful self-healing after traffic spikes.',
    tags: ['Rate Limiting', 'APIs', 'Traffic Control'],
    icon: Droplets,
    steps: 23,
    group: 'Rate Limiting',
  },
  {
    slug: 'fixed-window-counter',
    title: 'Fixed Window Counter',
    subtitle: 'Rate Limiting Algorithm',
    description:
      'Step through fixed time windows with a resetting counter. See requests get allowed ' +
      'and rejected, watch the counter reset on window expiry, and discover the ' +
      'thundering herd problem at window boundaries.',
    tags: ['Rate Limiting', 'APIs', 'Traffic Control'],
    icon: LayoutGrid,
    steps: 18,
    group: 'Rate Limiting',
  },
  {
    slug: 'sliding-window-log',
    title: 'Sliding Window Log',
    subtitle: 'Rate Limiting Algorithm',
    description:
      'Watch a timestamp log slide forward in time. See old entries expire and get cleaned, ' +
      'observe precise per-window enforcement with no thundering herd, ' +
      'and understand the memory trade-off vs Fixed Window.',
    tags: ['Rate Limiting', 'APIs', 'Traffic Control'],
    icon: ScrollText,
    steps: 16,
    group: 'Rate Limiting',
  },
];

export default function Dev() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 pt-24 pb-16 px-6">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <p className="text-blue-600 dark:text-blue-400 text-sm font-medium mb-3 tracking-wide">
            /dev
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Interactive Explainers
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl">
            Deep-dives into computer science concepts — visualised step by step so you can
            build real intuition, not just surface knowledge.
          </p>
        </div>

        {/* Cards grouped by topic */}
        {['Storage', 'Rate Limiting'].map(group => {
          const items = devItems.filter(i => i.group === group);
          return (
            <div key={group} className="mb-12">
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">
                {group}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.slug}
                      to={`/dev/${item.slug}`}
                      className="group relative flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800
                        bg-white dark:bg-gray-900 p-6 hover:border-blue-300 dark:hover:border-blue-700
                        hover:shadow-lg hover:shadow-blue-100 dark:hover:shadow-blue-950/30
                        transition-all duration-300"
                    >
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mb-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/60 transition-colors">
                        <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>

                      {/* Title */}
                      <div className="mb-1">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">{item.subtitle}</p>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2 flex-1">
                        {item.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {item.tags.map(tag => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                          {item.steps} interactive steps
                        </span>
                        <span className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 group-hover:gap-2 transition-all">
                          Explore <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>

                      {/* Hover glow */}
                      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ring-1 ring-blue-400/20 dark:ring-blue-500/20" />
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
